// 主人公の普段の致命の光 A案 (src/luna_spark_fx.ts) に、別の種類の FX を混ぜる比較版 (2026-10-23・開発版 ?lunaSparkMix=ash|ribbon)。
//   作者 (10-22): 量は「大量」。「エフェクトを複数種類混ぜた方が良さそうだなと思いました。」
//   「光の粒にハイ (灰) の欠片を混ぜるのと光の粒に細い光の帯を混ぜるを見てみたい」。記録 = docs/research/2026-10-23-luna-spark-mix.md。
//   見本の絵 (Codex) = そざい/concept/luna-spark-mix-1023/ash.png・ribbon.png。
// ash = 燃え尽きた紙のような黒い灰の尖った欠片 (見本どおり体は黒く、ふちをはっきり光らせ、背景の高さまで舞い上げて見せる)が、粒と一緒に出て少し浮き、ひらひら回りながらゆっくり落ちる。ふちだけ青白くほのかに光る。
// ribbon = 刺した点から、透けた光の帯が背中側の上へ昇り、先からほどけて消える (10-21 朝の版の帯の絵を使う。細くしすぎると稲妻の線に見えたので、見本に寄せて幅を残し淡くした)。
// 守ること: 白い効果線を描かない・点滅させない (明るさの揺らぎ無し・なめらかに灯ってなめらかに消える)・一瞬だけ強い光を作らない・色は常夜燈の青白だけ。
//   どれも時刻だけで決まる (乱数は種つき) = 同じ時刻なら同じ絵。
import { drawAdditive } from './exec_fx_sprites'
import type { CrackInput } from './luna_lamp_leak_fx'

export type SparkMix = 'none' | 'ash' | 'ribbon'
let mix: SparkMix = 'none'
/** game.ts の URL ブロックだけが呼ぶ。知らない値・指定なし = none (配布版)。 */
export function setSparkMix(v: string | null | undefined): SparkMix {
  mix = v === 'ash' || v === 'ribbon' ? v : 'none'
  if (mix === 'ribbon') preloadMixRibbons()
  return mix
}
export function sparkMix(): SparkMix { return mix }

/** 灰の欠片 (px は人の背丈 = 1 の時)。 */
export const ASH = {
  count: 32,
  /** 出る時刻の散らばり (秒)。粒 (0.08 秒) より少し遅れて広がる。 */
  birthSpread: 0.14,
  /** 飛び出す速さ px/秒 (下限・上限)・空気で止まる強さ (1/秒)。粒よりずっと遅い。 */
  speedMin: 160,
  speedMax: 640,
  drag: 3.2,
  /** 止まった後にゆっくり落ちる速さ px/秒 (下限・上限)。 */
  fallMin: 26,
  fallMax: 58,
  /** 横に揺れる幅 px・揺れの速さ (1/秒)。 */
  swayPx: 20,
  /** 大きさ px (長い辺)。 */
  sizeMin: 14,
  sizeMax: 28,
  /** 寿命 (下限・上限)。ふちの光は寿命の前半で弱まり、体は終わり 3 割でなめらかに消える。 */
  lifeMin: 2.0,
  lifeMax: 3.0,
  lifeSec: 3.2,
} as const

/** 細い光の帯。 */
export const MIX_RIBBON = {
  count: 3,
  stagger: 0.05,
  life: 0.85,
  /** 高さ px・幅は絵の比の何倍か (細くする)。 */
  height: 130,
  thin: 0.9,
  /** 昇る距離 px。 */
  rise: 90,
  /** 先がほどける (ちぎれた絵へ替える) 寿命の割合。 */
  tearAt: 0.45,
  alpha: 0.5,
} as const

/** 混ぜた時の全体の長さ (game.ts の FX の掃除)。 */
export function sparkMixLifeSec(base: number): number {
  return mix === 'ash' ? Math.max(base, ASH.lifeSec) : base
}

const clamp01 = (u: number): number => Math.max(0, Math.min(1, u))
function rnd(i: number, seed: number): number {
  const s = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453
  return s - Math.floor(s)
}
const smooth = (u: number): number => { const v = clamp01(u); return v * v * (3 - 2 * v) }

// ---- 灰の欠片の絵 (コードで一度だけ作る: 欠けた紙の形・黒い体・青白いふち) ----
const ASH_KINDS = 6
const ASH_PX = 64
type AshSprite = { body: HTMLCanvasElement; rim: HTMLCanvasElement }
let ashSprites: AshSprite[] | null = null
function ashShape(k: number): Array<[number, number]> {
  // 縦横の比がばらばらな、尖った欠片 (4〜6 頂点・見本の絵の形)
  const n = 4 + Math.floor(rnd(k, 41) * 3)
  const sx = 0.55 + rnd(k, 42) * 0.45
  const pts: Array<[number, number]> = []
  for (let j = 0; j < n; j++) {
    const a = (j / n) * Math.PI * 2 + (rnd(k * 13 + j, 43) - 0.5) * 0.5
    const r = 0.4 + rnd(k * 17 + j, 44) * 0.6
    pts.push([Math.cos(a) * r * sx, Math.sin(a) * r])
  }
  return pts
}
function buildAshSprites(): AshSprite[] | null {
  if (typeof document === 'undefined') return null
  const out: AshSprite[] = []
  for (let k = 0; k < ASH_KINDS; k++) {
    const pts = ashShape(k)
    const path = (g: CanvasRenderingContext2D): void => {
      g.beginPath()
      pts.forEach(([x, y], j) => { const px = ASH_PX / 2 + x * ASH_PX * 0.36; const py = ASH_PX / 2 + y * ASH_PX * 0.36; if (j) g.lineTo(px, py); else g.moveTo(px, py) })
      g.closePath()
    }
    const body = document.createElement('canvas')
    body.width = body.height = ASH_PX
    const b = body.getContext('2d')
    if (!b) return null
    path(b)
    const grad = b.createRadialGradient(ASH_PX * 0.45, ASH_PX * 0.45, 2, ASH_PX / 2, ASH_PX / 2, ASH_PX * 0.4)
    grad.addColorStop(0, 'rgb(56,58,70)')
    grad.addColorStop(1, 'rgb(20,21,28)')
    b.fillStyle = grad
    b.fill()
    // ふち: 体の外へ少しにじむ青白い光 (加算で重ねる・線ではなく、ぼけたふち)
    const rim = document.createElement('canvas')
    rim.width = rim.height = ASH_PX
    const r = rim.getContext('2d')
    if (!r) return null
    r.shadowColor = 'rgba(150,195,255,0.9)'
    r.shadowBlur = 7
    r.strokeStyle = 'rgba(196,224,255,0.95)'
    r.lineWidth = 2.4
    path(r)
    r.stroke()
    // 体の内側は消す (ふちの光だけ残す)
    r.globalCompositeOperation = 'destination-out'
    r.shadowBlur = 0
    r.save()
    r.translate(ASH_PX / 2, ASH_PX / 2)
    r.scale(0.86, 0.86)
    r.translate(-ASH_PX / 2, -ASH_PX / 2)
    path(r)
    r.fillStyle = '#000'
    r.fill()
    r.restore()
    out.push({ body, rim })
  }
  return out
}

/** 普段の致命の光に混ぜる物を描く (粒の後に呼ぶ)。 */
export function drawSparkMix(ctx: CanvasRenderingContext2D, c: CrackInput): void {
  if (mix === 'ash') drawAsh(ctx, c)
  else if (mix === 'ribbon') drawMixRibbons(ctx, c)
}

function drawAsh(ctx: CanvasRenderingContext2D, c: CrackInput): void {
  const age = c.ageSec
  if (age < 0 || age > ASH.lifeSec) return
  if (!ashSprites) ashSprites = buildAshSprites()
  if (!ashSprites) return
  const S = c.scale
  const away = c.dir
  const floorY = c.floorY - 2
  for (let i = 0; i < ASH.count; i++) {
    const t = age - rnd(i, 51) * ASH.birthSpread
    if (t < 0) continue
    const life = ASH.lifeMin + (ASH.lifeMax - ASH.lifeMin) * rnd(i, 52)
    if (t > life) continue
    // 向き: 背中側の真横〜真上の少し先 (多くは斜め上)。2割は主人公側の上へ少しだけ
    const back = rnd(i, 53) < 0.2
    const ang = back ? Math.PI - (0.9 + rnd(i, 54) * 0.6) : 0.25 + rnd(i, 54) * 1.3
    const sp = (ASH.speedMin + (ASH.speedMax - ASH.speedMin) * rnd(i, 55)) * (back ? 0.5 : 1) * S
    const vx = Math.cos(ang) * sp * away
    const vy = -Math.sin(ang) * sp
    const k = ASH.drag
    const e = Math.exp(-k * t)
    const fall = (ASH.fallMin + (ASH.fallMax - ASH.fallMin) * rnd(i, 56)) * S
    // 勢いは空気で止まり、止まった後は一定の速さでゆっくり落ちる (落ち始めはなめらかにつなぐ)
    const dx = vx * (1 - e) / k + Math.sin(t * (1.6 + rnd(i, 57) * 1.4) + rnd(i, 58) * 6) * ASH.swayPx * S * clamp01(t / 0.4)
    let dy = vy * (1 - e) / k + fall * (t - (1 - e) / k)
    let y = c.y + dy
    if (y > floorY) { y = floorY; dy = floorY - c.y }
    const size = (ASH.sizeMin + (ASH.sizeMax - ASH.sizeMin) * rnd(i, 59)) * S
    const spr = ashSprites[i % ASH_KINDS]!
    // ひらひら回る: 横幅を cos で縮めて裏返る感じを出す (明るさは変えない = 点滅させない)
    const spin = t * (2.2 + rnd(i, 60) * 2.6) + rnd(i, 61) * 6
    const flat = 0.25 + 0.75 * Math.abs(Math.cos(spin))
    const rot = t * (0.8 + rnd(i, 62) * 1.4) * (rnd(i, 63) < 0.5 ? -1 : 1) + rnd(i, 64) * 6
    const u = t / life
    const bodyA = smooth(t / 0.06) * (1 - smooth((u - 0.7) / 0.3))
    // ふちの光: 出た時がいちばん明るく、寿命の前半でなめらかに弱まり、弱い光を残して体と一緒に消える
    const rimA = bodyA * (0.45 + 0.55 * (1 - smooth(u / 0.55)))
    const x = c.x + dx
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(rot)
    ctx.scale(flat, 1)
    ctx.globalAlpha = bodyA * 0.95
    ctx.drawImage(spr.body, -size / 2, -size / 2, size, size)
    ctx.globalCompositeOperation = 'lighter'
    ctx.globalAlpha = rimA
    ctx.drawImage(spr.rim, -size / 2, -size / 2, size, size)
    ctx.restore()
  }
}

// ---- 細い光の帯 (10-21 朝の版の帯の絵 public/fx/luna-exec/ribbon_<種>_<段>.png を淡く使い回す) ----
const RIBBON_KINDS = 4
const RIBBON_ROOT_Y = 610 / 620
type Frame = { img: HTMLImageElement | null }
const ribbons: Frame[] = Array.from({ length: RIBBON_KINDS * 2 }, () => ({ img: null }))
let ribbonStarted = false
export function preloadMixRibbons(prefix = '/fx/luna-exec/'): void {
  if (ribbonStarted || typeof Image === 'undefined') return
  ribbonStarted = true
  ribbons.forEach((f, i) => {
    const img = new Image()
    img.onload = () => { f.img = img }
    img.src = `${prefix}ribbon_${i % RIBBON_KINDS}_${Math.floor(i / RIBBON_KINDS)}.png`
  })
}

function drawMixRibbons(ctx: CanvasRenderingContext2D, c: CrackInput): void {
  if (!ribbons.every(f => f.img)) return
  const S = c.scale
  const away = c.dir
  for (let i = 0; i < MIX_RIBBON.count; i++) {
    const t = c.ageSec - 0.02 - i * MIX_RIBBON.stagger
    if (t < 0 || t > MIX_RIBBON.life) continue
    const u = t / MIX_RIBBON.life
    const kind = (i + 1) % RIBBON_KINDS
    // 傾き: 真上から背中側へ 20〜55 度
    const tilt = (0.35 + rnd(i, 71) * 0.6) * away
    const rise = MIX_RIBBON.rise * S * (1 - Math.pow(1 - clamp01(u * 1.1), 2)) * (0.8 + rnd(i, 72) * 0.4)
    const rx = c.x + Math.sin(tilt) * rise
    const ry = c.y - Math.cos(tilt) * rise
    const grow = smooth(u / 0.3)
    const h = MIX_RIBBON.height * S * (0.75 + rnd(i, 73) * 0.4) * (0.3 + 0.7 * grow)
    const torn = u >= MIX_RIBBON.tearAt
    const blend = clamp01((u - MIX_RIBBON.tearAt) / 0.1)
    const img = ribbons[kind + (torn ? RIBBON_KINDS : 0)]!.img!
    const w = h * (img.width / img.height) * MIX_RIBBON.thin
    const a = smooth(t / 0.08) * (1 - smooth((u - 0.5) / 0.5)) * MIX_RIBBON.alpha
    const sway = Math.sin(t * 4 + i * 1.9) * 0.07
    const flip = away < 0
    if (torn && blend < 1) drawAdditive(ctx, ribbons[kind]!.img!, rx, ry, w, h, 0.5, RIBBON_ROOT_Y, a * (1 - blend), tilt + sway, flip)
    drawAdditive(ctx, img, rx, ry, w, h, 0.5, RIBBON_ROOT_Y, a * (torn ? blend : 1), tilt + sway, flip)
  }
}
